import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Tab,
  Tabs,
  Typography,
  Paper,
  alpha,
  useTheme,
  Chip,
  Stack,
  CircularProgress,
  Container,
} from "@mui/material";
import {
  Timeline as TimelineIcon,
  Update as UpdateIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { QK } from "../../../base/qk";
import { getCandidatesbyId, updateCandidate } from "../../../api/services/candidate";

const candidateStatusOptions = [
  "pending",
  "screening",
  "interview",
  "onboarding",
  "active",
  "inactive",
  "rejected",
  "terminated",
];

const applicationStatusOptions = [
  "New",
  "Application Progress",
  "App Rejected",
  "CV Submitted",
  "CV Rejected",
  "Interview",
  "Rejected PI",
  "Offer",
  "Not Accepted",
  "Holiday",
  "Placed",
];

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 0, pt: 2 }}>{children}</Box>}
    </div>
  );
};

const getStatusColor = (status) => {
  const statusMap = {
    "pending": "#F59E0B",
    "screening": "#3B82F6",
    "interview": "#8B5CF6",
    "onboarding": "#10B981",
    "active": "#22C55E",
    "inactive": "#94A3B8",
    "rejected": "#EF4444",
    "terminated": "#EF4444",
    "New": "#3B82F6",
    "Application Progress": "#F59E0B",
    "App Rejected": "#EF4444",
    "CV Submitted": "#8B5CF6",
    "CV Rejected": "#EF4444",
    "Interview": "#10B981",
    "Rejected PI": "#EF4444",
    "Offer": "#22C55E",
    "Not Accepted": "#F97316",
    "Holiday": "#EC4899",
    "Placed": "#22C55E",
  };
  return statusMap[status] || "#6B7280";
};

const StatusCard = ({ 
  title, 
  icon, 
  currentStatus, 
  selectedStatus, 
  options, 
  handleStatusChange, 
  handleStatusUpdate, 
  isUpdating,
  statusKey,
  chipColor,
}) => {
  const theme = useTheme();
  const color = chipColor || getStatusColor(currentStatus);

  return (
    <Card elevation={0} sx={{ border: "1px solid #E2E8F0", borderRadius: 2, mb: 2 }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "10px",
                bgcolor: alpha(color, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {icon}
            </Box>
            <Box>
              <Typography variant="caption" color="#6B7280" fontWeight={500}>
                {title}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={currentStatus || "Not Set"}
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    bgcolor: alpha(color, 0.12),
                    color: color,
                  }}
                />
                <Typography variant="caption" color="#94A3B8">•</Typography>
                <Typography variant="caption" color="#94A3B8">
                  {statusKey === 'interview_status' ? 'Application' : 'Candidate'} Status
                </Typography>
              </Stack>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Update {title}</InputLabel>
              <Select
                value={selectedStatus}
                label={`Update ${title}`}
                onChange={handleStatusChange}
                sx={{
                  borderRadius: "8px",
                  bgcolor: "#F8FAFC",
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#E2E8F0",
                    },
                    "&:hover fieldset": {
                      borderColor: "#94A3B8",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#2469F0",
                    },
                  },
                }}
              >
                {options.map((status) => (
                  <MenuItem key={status} value={status}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: getStatusColor(status),
                        }}
                      />
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={handleStatusUpdate}
              disabled={isUpdating}
              startIcon={isUpdating ? <CircularProgress size={18} /> : <UpdateIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                bgcolor: "#2469F0",
                px: 3,
                "&:hover": {
                  bgcolor: "#1A5BD4",
                  boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
                },
              }}
            >
              {isUpdating ? "Updating..." : "Update"}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const CandidatesTrack = ({ clickedId }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [tabValue, setTabValue] = useState(0);
  const [currentStatus, setCurrentStatus] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [currentInterviewStatus, setCurrentInterviewStatus] = useState("");
  const [selectedInterviewStatus, setSelectedInterviewStatus] = useState("");

  const { data: candidateData, isLoading } = useQuery(
    [QK.candidate, clickedId],
    () => getCandidatesbyId(clickedId),
    {
      enabled: !!clickedId,
      onSuccess: (data) => {
        if (data?.data?.status) {
          setCurrentStatus(data.data.status);
          setSelectedStatus(data.data.status);
        }
        if (data?.data?.interview_status) {
          setCurrentInterviewStatus(data.data.interview_status);
          setSelectedInterviewStatus(data.data.interview_status);
        }
      },
    }
  );

  const statusUpdateMutation = useMutation(
    (status) => {
      const formData = new FormData();
      formData.append("status", status);
      return updateCandidate(formData, clickedId);
    },
    {
      onSuccess: () => {
        enqueueSnackbar("Candidate status updated successfully", { variant: "success" });
        queryClient.invalidateQueries([QK.candidate, clickedId]);
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || "Failed to update status";
        enqueueSnackbar(errorMessage, { variant: "error" });
      },
    }
  );

  const interviewUpdateMutation = useMutation(
    (status) => {
      const formData = new FormData();
      formData.append("interview_status", status);
      return updateCandidate(formData, clickedId);
    },
    {
      onSuccess: () => {
        enqueueSnackbar("Interview status updated successfully", { variant: "success" });
        queryClient.invalidateQueries([QK.candidate, clickedId]);
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || "Failed to update interview status";
        enqueueSnackbar(errorMessage, { variant: "error" });
      },
    }
  );

  const handleStatusChange = (event) => {
    setSelectedStatus(event.target.value);
  };

  const handleInterviewStatusChange = (event) => {
    setSelectedInterviewStatus(event.target.value);
  };

  const handleStatusUpdate = () => {
    if (selectedStatus !== currentStatus) {
      statusUpdateMutation.mutate(selectedStatus);
    } else {
      enqueueSnackbar("Status is already set to this value", { variant: "info" });
    }
  };

  const handleInterviewStatusUpdate = () => {
    if (selectedInterviewStatus !== currentInterviewStatus) {
      interviewUpdateMutation.mutate(selectedInterviewStatus);
    } else {
      enqueueSnackbar("Interview status is already set to this value", { variant: "info" });
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 0 }}>
      <Box sx={{ width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2.5,
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              bgcolor: alpha("#2469F0", 0.08),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CheckCircleIcon sx={{ color: "#2469F0", fontSize: 18 }} />
          </Box>
          <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
            Candidate Status Management
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            border: "1px solid #E2E8F0",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              minHeight: 48,
              bgcolor: "#F8FAFC",
              borderBottom: "1px solid #E2E8F0",
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8rem",
                py: 1,
                minHeight: 48,
                color: "#64748B",
                "&.Mui-selected": {
                  color: "#2469F0",
                },
              },
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "3px 3px 0 0",
                bgcolor: "#2469F0",
              },
            }}
          >
            <Tab
              icon={<PersonIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Candidate Status"
            />
            <Tab
              icon={<WorkIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Interview Status"
            />
          </Tabs>

          <Box sx={{ p: 2.5 }}>
            <TabPanel value={tabValue} index={0}>
              <StatusCard
                title="Candidate Status"
                icon={<PersonIcon sx={{ color: getStatusColor(currentStatus), fontSize: 22 }} />}
                currentStatus={currentStatus}
                selectedStatus={selectedStatus}
                options={candidateStatusOptions}
                handleStatusChange={handleStatusChange}
                handleStatusUpdate={handleStatusUpdate}
                isUpdating={statusUpdateMutation.isLoading}
                statusKey="status"
                chipColor={getStatusColor(currentStatus)}
              />
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              <StatusCard
                title="Interview Status"
                icon={<WorkIcon sx={{ color: getStatusColor(currentInterviewStatus), fontSize: 22 }} />}
                currentStatus={currentInterviewStatus}
                selectedStatus={selectedInterviewStatus}
                options={applicationStatusOptions}
                handleStatusChange={handleInterviewStatusChange}
                handleStatusUpdate={handleInterviewStatusUpdate}
                isUpdating={interviewUpdateMutation.isLoading}
                statusKey="interview_status"
                chipColor={getStatusColor(currentInterviewStatus)}
              />
            </TabPanel>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default CandidatesTrack;